import { FC } from 'react';
import { Controller, FormProvider } from 'react-hook-form';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import {
  Autocomplete,
  FormGroup,
} from '@savgroup-front-common/core/atoms/Form';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { SUPPORTED_ICON_POSITIONS } from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { useStepOrchestratorContext } from '@savgroup-front-common/core/molecules/StepsOrchestrator';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';
import adaptReasonsToOptions from '@/helpers/adaptReasonsToOptions';
import { NewStepLayoutProfessional } from '@/view/app/NewLayout/NewStepLayout/NewStepLayoutProfessional';
import {
  PROFESSIONAL_STEP_CONFIG,
  PROFESSIONAL_STEP_PROGRESS,
} from '@/view/app/NewLayout/ProfessionalRoutes.config';

import { PageFooterContainer } from '../../../../../../app/NewLayout/NewStepLayout';

import messages from './messages';
import useClaimGroupReason from './ReasonPage.hooks';
import { IssuesReasonsPageForm } from './ReasonPage.types';

const ReasonPage: FC = () => {
  const { reasons, onSubmit, formContext, isLoading, isSubmitLoading } =
    useClaimGroupReason();

  const {
    control,
    formState: { errors, isValid },
  } = formContext;

  const options = adaptReasonsToOptions({ reasons });

  const { previousStep } = useStepOrchestratorContext<IssuesReasonsPageForm>();

  if (isLoading) {
    return <BaseLoader />;
  }

  return (
    <FormProvider {...formContext}>
      <form onSubmit={onSubmit}>
        <NewStepLayoutProfessional
          stepName={PROFESSIONAL_STEP_CONFIG.PROFESSIONAL_ISSUES_REASONS_PAGE}
          customSteps={PROFESSIONAL_STEP_PROGRESS}
          minHeight
        >
          <FormGroup dataTestId="dropdown-select-reason">
            <Controller
              control={control}
              name="reason"
              render={({ field }) => (
                <Autocomplete
                  name={field.name}
                  options={options}
                  isRequired
                  ref={field.ref}
                  onChange={field.onChange}
                  value={field.value}
                  errors={errors}
                  label={safeFormattedIntlString(messages.selectReason)}
                  shouldAutoSelectFirstOptions={options.length === 1}
                />
              )}
            />
          </FormGroup>
        </NewStepLayoutProfessional>

        <PageFooterContainer>
          <Button
            primary
            type={BUTTON_TYPES.BUTTON}
            icon={<ArrowNakedRightIcon />}
            position={SUPPORTED_ICON_POSITIONS.RIGHT}
            disabled={!isValid}
            isLoading={isSubmitLoading}
            onClick={onSubmit}
            dataTestId="nextButton"
          >
            <SafeFormattedMessageWithoutSpread message={messages.nextButton} />
          </Button>

          <Button
            type={BUTTON_TYPES.BUTTON}
            tertiary
            onClick={() => {
              previousStep();
            }}
            icon={<ArrowNakedLeftIcon />}
            position={SUPPORTED_ICON_POSITIONS.LEFT}
            dataTestId="previousButton"
          >
            <SafeFormattedMessageWithoutSpread
              message={messages.previousButton}
            />
          </Button>
        </PageFooterContainer>
      </form>
    </FormProvider>
  );
};

export default ReasonPage;
